import React from 'react';
import * as Tone from "tone";
import 'bootstrap/dist/css/bootstrap.css';
import './App.scss'
import Step from "./Step";
import Header from "./Header";
import {defaults, pitchLatency, pitchRates} from "./defaults";
import {exportLoop} from "./exportLoop";
import {useHotkeys} from 'react-hotkeys-hook'
import {randomize} from "./randomize";
import {createMonoVoice} from "./mono";

type PlayerType = 'normal' | 'hi' | 'lo' | 'reverse';

const main = new Tone.Volume(-0.5).toDestination()
const filter: Tone.Filter = new Tone.Filter(200, "highpass").connect(main)

// Slices are played by a fresh ToneBufferSource per hit rather than by a shared
// Tone.Player. Assigning `Player.playbackRate` calls cancelStop() on every source
// still sounding (see Player.js), which un-schedules that slice's end — moving the
// speed slider mid-loop would leave slices ringing on for up to a bar, phasing
// against the following loop cycles. A per-hit source takes its rate at creation,
// so nothing already in flight is ever retuned.
//
// One dedicated buffer pair (normal + reversed) per loaded sample — the
// per-sample equivalent of the old players[type][beatIndex] matrix; hi/lo
// share the normal buffer since their pitch comes from the output chain/rate.
const buffers: Record<'normal' | 'reverse', Tone.ToneAudioBuffer[]> = {
    'normal': [],
    'reverse': [],
}

const outputs: Record<PlayerType, Tone.ToneAudioNode | null> = {
    'normal': null,
    'hi': null,
    'lo': null,
    'reverse': null,
}

const bufferFor = (playerType: PlayerType, sampleIndex: number) =>
    playerType === 'reverse' ? buffers.reverse[sampleIndex] : buffers.normal[sampleIndex]

// every hit goes through the one monophonic voice, whatever its player type
const playMono = createMonoVoice()

function App() {
    const [sampleUrls, setSampleUrls] = React.useState<string[]>([])
    const sampleUrlsRef: any = React.useRef(null)
    sampleUrlsRef.current = sampleUrls

    const [activeStep, setActiveStep] = React.useState(-1)
    const activeStepRef: any = React.useRef(-1)
    activeStepRef.current = activeStep

    const [busy, setBusy] = React.useState(true)
    const busyRef: any = React.useRef(null)
    busyRef.current = busy

    const [bpm, setBpm] = React.useState(0)
    const bpmRef: any = React.useRef(null)
    bpmRef.current = bpm

    const [playing, setPlaying] = React.useState(false)
    const playingRef: any = React.useRef(null)
    playingRef.current = playing

    const [exporting, setExporting] = React.useState(false)
    const exportingRef: any = React.useRef(null)
    exportingRef.current = exporting

    const [initialized, setInitialized] = React.useState(false)
    const initializedRef: any = React.useRef(null)
    initializedRef.current = initialized

    // guards the stop-timeout in startStop() from clearing `busy` mid-init
    const initInFlightRef = React.useRef(false)

    const [slices, setSlices] = React.useState<number[]>([...defaults.slices])
    const slicesRef: any = React.useRef(null)
    slicesRef.current = slices

    // which loaded sample each step plays (index into buffers.normal)
    const [samples, setSamples] = React.useState<number[]>([...defaults.samples])
    const samplesRef: any = React.useRef(null)
    samplesRef.current = samples

    const [retrigs, setRetrigs] = React.useState<number[]>([...defaults.retrigs])
    const retrigsRef: any = React.useRef(null)
    retrigsRef.current = retrigs

    const [playerTypes, setPlayerTypes] = React.useState<PlayerType[]>([...defaults.playerTypes as PlayerType[]])
    const playerTypesRef: any = React.useRef(null)
    playerTypesRef.current = playerTypes

    const [locks, setLocks] = React.useState<boolean[]>([...defaults.locks])
    const locksRef: any = React.useRef(null)
    locksRef.current = locks

    // -50..+50 %, varispeed: scales transport tempo and sample playback rate together
    const [speed, setSpeed] = React.useState<number>(0)
    const speedRef: any = React.useRef(0)
    speedRef.current = speed

    // tempo the sample was analysed at, before the speed slider is applied
    const baseBpmRef = React.useRef<number>(0)

    const [percentRetrig, setPercentRetrig] = React.useState<number>(10)
    const percentRetrigRef: any = React.useRef(null)
    percentRetrigRef.current = percentRetrig

    const [percentNext, setPercentNext] = React.useState<number>(10)
    const percentNextRef: any = React.useRef(null)
    percentNextRef.current = percentNext

    const [percentSwitch, setPercentSwitch] = React.useState<number>(10)
    const percentSwitchRef: any = React.useRef(null)
    percentSwitchRef.current = percentSwitch

    const [percentRandom, setPercentRandom] = React.useState<number>(20)
    const percentRandomRef: any = React.useRef(null)
    percentRandomRef.current = percentRandom

    const [percentHi, setPercentHi] = React.useState<number>(10)
    const percentHiRef: any = React.useRef(null)
    percentHiRef.current = percentHi

    const [percentLo, setPercentLo] = React.useState<number>(10)
    const percentLoRef: any = React.useRef(null)
    percentLoRef.current = percentLo

    const [percentRev, setPercentRev] = React.useState<number>(0)
    const percentRevRef: any = React.useRef(null)
    percentRevRef.current = percentRev

    // Memory cleanup für Blob-URLs
    const prevSampleUrlsRef = React.useRef<string[]>([])
    React.useEffect(() => {
        for (const url of prevSampleUrlsRef.current) {
            if (url.startsWith('blob:')) {
                URL.revokeObjectURL(url)
            }
        }
        prevSampleUrlsRef.current = sampleUrls
    }, [sampleUrls])

    const doExport = async () => {
        if (!initializedRef.current || busyRef.current || exportingRef.current) {
            return
        }
        setExporting(true)
        try {
            await exportLoop({
                normalBuffers: [...buffers.normal],
                reverseBuffers: [...buffers.reverse],
                slices: [...slicesRef.current],
                samples: [...samplesRef.current],
                retrigs: [...retrigsRef.current],
                playerTypes: [...playerTypesRef.current],
                bpm: bpmRef.current,
                speed: speedRef.current,
            })
        } finally {
            setExporting(false)
        }
    }

    const reset = () => {
        setLocks([...defaults.locks])
        setSlices([...defaults.slices])
        setSamples([...defaults.samples])
        setRetrigs([...defaults.retrigs])
        setPlayerTypes([...defaults.playerTypes as PlayerType[]])
    }

    const doRandomize = () => {
        randomize(
            {
                retrig: percentRetrigRef.current,
                nextSlice: percentNextRef.current,
                randomSlice: percentRandomRef.current,
                switchSample: percentSwitchRef.current,
                hiPitch: percentHiRef.current,
                loPitch: percentLoRef.current,
                reverse: percentRevRef.current,
            },
            locksRef,
            slicesRef,
            samplesRef,
            retrigsRef,
            playerTypesRef,
            Math.max(1, buffers.normal.length),
            setSlices,
            setSamples,
            setRetrigs,
            setPlayerTypes,
        )
    }

    // `duration` and `offset` are buffer time, everything else is real time —
    // playing `duration` seconds of material at `rate` takes `duration / rate`
    const playStep = (playerType: PlayerType, sampleIndex: number, time: number, offset: number, duration: number, retrig: number, rate: number) => {
        const buffer = bufferFor(playerType, sampleIndex)
        const output = outputs[playerType]

        if (!buffer || output === null) {
            throw new Error('Player not initialized')
        }

        playMono({buffer, output, time, offset, duration, rate, pitchRate: pitchRates[playerType] || 1, retrig})
    }

    const setTempo = (bpm: number) => {
        Tone.getTransport().bpm.value = bpm
        setBpm(bpm)
    }

    // Only the tempo is touched here — the rate itself is picked up by the next
    // slice that starts, so slices already sounding play out at the rate they
    // were started with instead of being retuned mid-flight.
    const setSpeedValue = (value: number) => {
        speedRef.current = value
        setSpeed(value)
        if (baseBpmRef.current > 0) {
            setTempo(baseBpmRef.current * (1 + (value / 100)))
        }
    }

    // Buffers decode fine on a suspended AudioContext, so init() must never await
    // Tone.start(): a file dragged in from the desktop is not a user gesture (drop
    // is not an activation-triggering event), so resume() would stay pending
    // forever and leave `busy` stuck at true, i.e. PLAY disabled. The context is
    // resumed in startStop() instead, from a real click/keypress.
    const init = async (urls?: string[]) => {
        // sampleUrlsRef only catches up on the next render, so trust the urls we were handed
        const sampleList = urls ?? sampleUrlsRef.current
        if (!sampleList || sampleList.length === 0) {
            return
        }
        sampleUrlsRef.current = sampleList

        initInFlightRef.current = true
        setBusy(true)
        console.time('init audio engine');

        try {
            const pitchUp = new Tone.PitchShift({
                pitch: 6,
                windowSize: 0.03
            }).connect(filter)

            buffers.normal = sampleList.map((url: string) => new Tone.ToneAudioBuffer(url))
            buffers.reverse = sampleList.map((url: string) => {
                const reverseBuffer = new Tone.ToneAudioBuffer(url)
                reverseBuffer.reverse = true // applied again once the buffer lands
                return reverseBuffer
            })

            outputs['normal'] = filter
            outputs['hi'] = pitchUp
            outputs['lo'] = filter
            outputs['reverse'] = filter

            await Tone.loaded()

            // the first sample owns the grid: it sets the tempo, and every other
            // sample is rate-fitted (varispeed) so its 16 slices land on the
            // same 16 steps regardless of its own length
            const sampleDurations = buffers.normal.map(buffer => buffer.duration)
            const sliceDurations = sampleDurations.map(duration => duration / 16)
            const fitRates = sampleDurations.map(duration => duration / sampleDurations[0])

            const bpmValue = (60 / (sampleDurations[0] / 8)) // 2 bars = 8 beats

            baseBpmRef.current = bpmValue
            setTempo(bpmValue)
            // a new sample brings its own tempo, so drop back to unmodified speed
            setSpeedValue(0)

            Tone.getTransport().cancel();

            Tone.getTransport().scheduleRepeat(
                (time) => {
                    if (!playingRef.current || busyRef.current) {
                        return
                    }

                    activeStepRef.current++
                    if (activeStepRef.current > 15) {
                        activeStepRef.current = 0
                    }

                    setActiveStep(activeStepRef.current)

                    const sliceIndex = slicesRef.current[activeStepRef.current]
                    const retrig = retrigsRef.current[activeStepRef.current]
                    const playerType: PlayerType = playerTypesRef.current[activeStepRef.current]
                    const sampleIndex = Math.min(samplesRef.current[activeStepRef.current] || 0, buffers.normal.length - 1)

                    // fit stretches this sample's grid onto the master (first sample's)
                    // grid, on top of the varispeed rate
                    const fit = fitRates[sampleIndex]
                    const rate = (1 + (speedRef.current / 100)) * fit

                    const nextStepEmpty = (slicesRef.current[activeStepRef.current + 1] !== undefined && slicesRef.current[activeStepRef.current + 1] === -1)

                    // how much sample material fits in one step, in buffer time. Taken
                    // from the base tempo because speed cancels out (the transport is
                    // already `base * rate` fast), and because baseBpmRef is written
                    // synchronously — bpm state lags a render behind the slider, which
                    // would truncate one step mid-drag.
                    const stepBufferSeconds = ((60 / (baseBpmRef.current || bpmRef.current)) / 2) * fit
                    let playDuration = Math.min(sliceDurations[sampleIndex], stepBufferSeconds)

                    if (nextStepEmpty) {
                        playDuration *= 2
                    }

                    let offset = sliceIndex * sliceDurations[sampleIndex]

                    if (sliceIndex > -1) {
                        const startTime = Math.max(time - (pitchLatency[playerType] || 0), Tone.getContext().currentTime)
                        playStep(playerType, sampleIndex, startTime, offset, playDuration, retrig, rate)
                    }
                },
                '8n',
            )

            initializedRef.current = true
            setInitialized(true)
        } catch (e) {
            console.error('init audio engine failed', e)
        } finally {
            console.timeEnd('init audio engine');
            initInFlightRef.current = false
            setBusy(false)
        }
    }

    const startStop = async () => {
        if (busyRef.current) {
            return false
        }
        if (!initializedRef.current) {
            await init()
            if (!initializedRef.current) {
                return false
            }
        }
        // this is the user gesture that unblocks the AudioContext
        await Tone.start()
        setPlaying(!playingRef.current)
        if (playingRef.current) {
            setBusy(true)
            Tone.getTransport().stop()
            setTimeout(() => {
                activeStepRef.current = -1
                setActiveStep(-1)
                if (!initInFlightRef.current) {
                    setBusy(false)
                }
            }, 200)
            return
        }
        Tone.getTransport().start()
    }

    useHotkeys('space', (e) => {
        e.preventDefault()
        startStop()
    }, [])

    useHotkeys('r, m', (e) => {
        e.preventDefault()
        doRandomize()
    }, [])

    useHotkeys('esc', (e) => {
        e.preventDefault()
        reset()
    }, [])

    useHotkeys('shift+e', (e) => {
        e.preventDefault()
        doExport()
    }, [])

    useHotkeys('1,2,3,4', (e) => {
        e.preventDefault()
        const index = parseInt(e.key) - 1
        locksRef.current[index] = !locksRef.current[index]
        setLocks([...locksRef.current])
    }, [])

    // keep the base in sync so the speed slider stays relative to the nudged tempo
    const nudgeTempo = (delta: number) => {
        const next = bpmRef.current + delta
        baseBpmRef.current = next / (1 + (speedRef.current / 100))
        setTempo(next)
    }

    useHotkeys('up', (e) => {
        e.preventDefault()
        nudgeTempo(1)
    }, [])

    useHotkeys('down', (e) => {
        e.preventDefault()
        nudgeTempo(-1)
    }, [])

    const sliderStyle = {maxHeight: 18};

    return (
        <div className="container">

            <div className="row pt-4">
                <div className="col">
                    <h1>
                        BRE<span style={{color: '#f8631f'}}></span>KC0R<span style={{color: '#ffc309'}}>3</span>
                        M<span style={{color: '#04b99e'}}></span>NGL<span style={{color: '#01aef3'}}>3</span>R
                    </h1>
                </div>
            </div>

            <Header
                setSamples={setSampleUrls}
                init={init}
                playing={playing}
                busy={busy}
                startStop={startStop}
                randomize={doRandomize}
                reset={reset}
                onExport={doExport}
                exporting={exporting}
                initialized={initialized}
                locksRef={locksRef}
                setLocks={setLocks}
            />

            <div className="row">
                <div className="col">
                    <div className="steps">
                        {Array.from(Array(16).keys()).map((step: number) => (
                            <Step
                                key={`step-${step}`}
                                step={step}
                                activeStep={activeStep}
                                slices={slices}
                                samples={samples}
                                retrigs={retrigs}
                                playerTypes={playerTypes}
                                locks={locks}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="row mb-2">
                <div className="col col-md-6">
                    <label className="form-label mb-1">
                        {speed < 0 ? '-' : '+'}{String(Math.abs(speed)).padStart(2, '0')}% SPEED
                    </label>
                </div>
                <div className="col">
                    <input value={speed} onChange={(e: any) => {
                        setSpeedValue(parseInt(e.target.value, 10))
                    }} type="range" className="form-control" min={-50} max={50} step={1} style={sliderStyle} />
                </div>
            </div>

            <div className="row mb-2">
                <div className="col col-md-6">
                    <label className="form-label mb-1">{String(percentRetrig).padStart(3, '0')}% RETRIG</label>
                </div>
                <div className="col">
                    <input value={percentRetrig} onChange={(e: any) => {
                        setPercentRetrig(e.target.value)
                    }} type="range" className="form-control" min={0} max={100} step={1} style={sliderStyle} />
                </div>
            </div>

            <div className="row mb-1">
                <div className="col col-md-6">
                    <label className="form-label mb-1">{String(percentRandom).padStart(3, '0')}% RANDOM</label>
                </div>
                <div className="col">
                    <input value={percentRandom} onChange={(e: any) => {
                        setPercentRandom(e.target.value)
                    }} type="range" className="form-control" min={0} max={100} step={1} style={sliderStyle} />
                </div>
            </div>

            <div className="row mb-2">
                <div className="col col-md-6">
                    <label className="form-label mb-1">{String(percentNext).padStart(3, '0')}% NEXT</label>
                </div>
                <div className="col">
                    <input value={percentNext} onChange={(e: any) => {
                        setPercentNext(e.target.value)
                    }} type="range" className="form-control" min={0} max={100} step={1} style={sliderStyle} />
                </div>
            </div>

            <div className="row mb-2">
                <div className="col col-md-6">
                    <label className="form-label mb-1">{String(percentSwitch).padStart(3, '0')}% SWITCH</label>
                </div>
                <div className="col">
                    <input value={percentSwitch} onChange={(e: any) => {
                        setPercentSwitch(e.target.value)
                    }} type="range" className="form-control" min={0} max={100} step={1} style={sliderStyle} />
                </div>
            </div>

            <div className="row mb-1">
                <div className="col col-md-6">
                    <label className="form-label mb-1">{String(percentHi).padStart(3, '0')}% HIGH</label>
                </div>
                <div className="col">
                    <input value={percentHi} onChange={(e: any) => {
                        setPercentHi(e.target.value)
                    }} type="range" className="form-control" min={0} max={100} step={1} style={sliderStyle} />
                </div>
            </div>

            <div className="row mb-1">
                <div className="col col-md-6">
                    <label className="form-label mb-1">{String(percentLo).padStart(3, '0')}% LOW</label>
                </div>
                <div className="col">
                    <input value={percentLo} onChange={(e: any) => {
                        setPercentLo(e.target.value)
                    }} type="range" className="form-control" min={0} max={100} step={1} style={sliderStyle} />
                </div>
            </div>

            <div className="row mb-4">
                <div className="col col-md-6">
                    <label className="form-label mb-1">{String(percentRev).padStart(3, '0')}% REV</label>
                </div>
                <div className="col">
                    <input value={percentRev} onChange={(e: any) => {
                        setPercentRev(e.target.value)
                    }} type="range" className="form-control" min={0} max={100} step={1} style={sliderStyle} />
                </div>
            </div>

            <div className="row mt-3">
                <div className="col">
                    <p className="text-uppercase">
                        space, shift+e,<br />
                        m, esc, 1,2,3,4
                    </p>
                </div>
            </div>

        </div>
    );
}

export default App;
